/**
 * WebSocket-backed {@link AppTransport}.
 *
 * Browser-safe: uses the standard `WebSocket` global (no `ws` dependency, no
 * `node:*` imports), so it runs unchanged in an embedded app's iframe and in a
 * lightweight Node 22+ service. The injected session identity is presented as a
 * bearer **subprotocol** — the same `skaile-bearer.<base64url>` scheme the
 * platform gateway already decodes for runner connections — so the gateway
 * verifies identity at the WS upgrade, before any `app_hello` is processed.
 *
 * Frames are bare JSON of the app-channel messages (no `MessageV2` envelope),
 * matching the runner peer's on-the-wire convention.
 *
 * @category App SDK
 * @since 3.4.0
 */

import type { AppClientMessage, AppServerMessage, AppTransport } from "./transport.js";

/** Matches `BEARER_SUBPROTOCOL_PREFIX` in `@skaile/workspaces/transport`'s ws/auth. */
const BEARER_SUBPROTOCOL_PREFIX = "skaile-bearer.";

/** Encode a bearer token as a WS subprotocol token (base64url, no padding). */
function encodeBearerSubprotocol(token: string): string {
  // Encode via UTF-8 bytes so a non-ASCII token doesn't make `btoa` throw
  // (btoa is Latin-1 only). The gateway decodes the mirror of this.
  const binary = String.fromCharCode(...new TextEncoder().encode(token));
  const b64 = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return BEARER_SUBPROTOCOL_PREFIX + b64;
}

/** Minimal structural view of the parts of the `WebSocket` API the transport uses. */
type WebSocketLike = {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: "open", handler: () => void): void;
  addEventListener(type: "close", handler: () => void): void;
  addEventListener(type: "error", handler: () => void): void;
  addEventListener(type: "message", handler: (ev: { data: unknown }) => void): void;
};

/** Constructor shape for a `WebSocket` implementation (global or injected). */
export type WebSocketFactory = new (url: string, protocols?: string | string[]) => WebSocketLike;

/**
 * Supplies a session identity token for one connection attempt.
 *
 * The gateway's token is **single-use** — it is consumed at `app_hello` — so a
 * reconnecting transport cannot replay the one it opened with. Pass a provider
 * (typically a call to `resolveAppConnectionParams`) and every `connect()` mints
 * a fresh token.
 *
 * @category App SDK
 * @since 3.5.0
 */
export type SessionTokenProvider = () => string | Promise<string>;

/**
 * Options for {@link createWebSocketAppTransport}.
 *
 * @category App SDK
 * @since 3.4.0
 */
export interface WebSocketAppTransportOptions {
  /** The gateway app-channel URL (e.g. `wss://platform/app-connect`). */
  url: string;
  /**
   * The injected session identity token, or a {@link SessionTokenProvider} that
   * yields a fresh one per connection attempt. Presented as the WS bearer
   * subprotocol; the gateway verifies it and resolves the session/user.
   *
   * A bare string cannot survive a reconnect (the gateway consumes the token at
   * `app_hello`) — pass a provider whenever `reconnect` is enabled on the peer.
   */
  sessionToken: string | SessionTokenProvider;
  /**
   * WebSocket implementation. Defaults to the `WebSocket` global (browser /
   * Node 22+). Injectable for tests or non-standard runtimes.
   */
  WebSocketImpl?: WebSocketFactory;
  /** Connect timeout in ms. Default 10000. */
  connectTimeoutMs?: number;
}

const OPEN = 1;

/**
 * Create a WebSocket-backed {@link AppTransport}. The session token is sent as a
 * bearer subprotocol at connect time; app-channel messages are JSON-framed.
 *
 * @category App SDK
 * @since 3.4.0
 */
export function createWebSocketAppTransport(options: WebSocketAppTransportOptions): AppTransport {
  const { url, sessionToken, connectTimeoutMs = 10_000 } = options;
  const resolved =
    options.WebSocketImpl ?? (globalThis as { WebSocket?: WebSocketFactory }).WebSocket;
  if (!resolved) {
    throw new Error(
      "No WebSocket implementation available. Pass `WebSocketImpl` in a runtime without a global WebSocket.",
    );
  }
  const WebSocketImpl: WebSocketFactory = resolved;

  let ws: WebSocketLike | null = null;
  const messageHandlers = new Set<(m: AppServerMessage) => void>();
  const disconnectHandlers = new Set<() => void>();

  function connect(): Promise<void> {
    // A provider is resolved per attempt — the gateway consumes the token at
    // `app_hello`, so a reconnect must present a new one. A bare string opens the
    // socket synchronously, so `connect()` still constructs it before it yields.
    if (typeof sessionToken === "function") {
      return Promise.resolve(sessionToken()).then(openSocket);
    }
    return openSocket(sessionToken);
  }

  function openSocket(token: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let opened = false;
      const socket = new WebSocketImpl(url, [encodeBearerSubprotocol(token)]);
      ws = socket;

      const timer = setTimeout(() => {
        if (opened) return;
        socket.close(4008, "connect timeout");
        reject(new Error(`app transport connect timed out after ${connectTimeoutMs}ms`));
      }, connectTimeoutMs);

      socket.addEventListener("open", () => {
        opened = true;
        clearTimeout(timer);
        resolve();
      });
      socket.addEventListener("error", () => {
        if (!opened) {
          clearTimeout(timer);
          reject(new Error("app transport connection error"));
        }
      });
      socket.addEventListener("close", () => {
        clearTimeout(timer);
        if (ws === socket) ws = null;
        for (const h of disconnectHandlers) h();
      });
      socket.addEventListener("message", (ev) => {
        let parsed: AppServerMessage;
        try {
          parsed = JSON.parse(String(ev.data)) as AppServerMessage;
        } catch {
          return;
        }
        for (const h of messageHandlers) h(parsed);
      });
    });
  }

  return {
    connect,
    async disconnect() {
      const socket = ws;
      ws = null;
      socket?.close(1000, "client closed");
    },
    send(message: AppClientMessage) {
      if (!ws || ws.readyState !== OPEN) {
        throw new Error("app transport is not connected");
      }
      ws.send(JSON.stringify(message));
    },
    onMessage(handler) {
      messageHandlers.add(handler);
      return () => messageHandlers.delete(handler);
    },
    onDisconnect(handler) {
      disconnectHandlers.add(handler);
      return () => disconnectHandlers.delete(handler);
    },
    get connected() {
      return ws?.readyState === OPEN;
    },
  };
}
